import { useCallback, useState, type RefObject } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  AID_STUDIO_STEP,
  AID_STUDIO_STEPS,
} from '../aid-studio-details.constants';

const STEP_VALUES: string[] = AID_STUDIO_STEPS.map((s) => s.value);
const DEFAULT_STEP = AID_STUDIO_STEPS[0].value;

interface ActiveStepRef {
  hasChanges?: boolean;
  save?: () => Promise<void> | void;
  discard?: () => void;
}

interface UseStepNavigationParams {
  isNewModel: boolean;
  stepParam: string | undefined;
  basePath: string;
  activeStepRef: RefObject<ActiveStepRef | null>;
  isUserView?: boolean;
}

export const useStepNavigation = ({
  isNewModel,
  stepParam,
  basePath,
  activeStepRef,
  isUserView,
}: UseStepNavigationParams) => {
  const navigate = useNavigate();
  const [stepHasChanges, setStepHasChanges] = useState(false);

  const defaultStep =
    isUserView && !isNewModel ? AID_STUDIO_STEP.RESULTS : DEFAULT_STEP;

  const setActiveStep = useCallback(
    (step: string): void => {
      setStepHasChanges(false);
      const path = step === defaultStep ? basePath : `${basePath}/${step}`;

      navigate(path, { replace: true });
    },
    [navigate, basePath, defaultStep]
  );

  const isValidStepParam = !!stepParam && STEP_VALUES.includes(stepParam);

  const activeStep = isNewModel
    ? DEFAULT_STEP
    : isValidStepParam
    ? stepParam
    : defaultStep;

  const activeStepIndex = AID_STUDIO_STEPS.findIndex(
    (s) => s.value === activeStep
  );

  const isLastStep = activeStepIndex === AID_STUDIO_STEPS.length - 1;

  const saveCurrentStep = useCallback(async (): Promise<void> => {
    const step = activeStepRef.current;

    if (step?.hasChanges) {
      await step.save?.();
    }
  }, [activeStepRef]);

  const handleStepChange = useCallback(
    async (stepValue: string): Promise<void> => {
      if (stepValue !== activeStep) {
        await saveCurrentStep();
        setActiveStep(stepValue);
      }
    },
    [activeStep, saveCurrentStep, setActiveStep]
  );

  const handleStepDirtyChange = useCallback((dirty: boolean) => {
    setStepHasChanges(dirty);
  }, []);

  return {
    activeStep,
    visibleSteps: AID_STUDIO_STEPS,
    isLastStep,
    stepHasChanges,
    handleStepChange,
    handleStepDirtyChange,
  };
};
